// Nhóm "Hạng": ngày ra mắt + bảng hạng có màu từng hạng (tên, ngưỡng đ, hệ số ×, quyền lợi) + xem trước Xu thực nhận.
import { Alert, Button, DatePicker, Input, InputNumber, Select, Table } from "antd";
import { faRankingStar, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import dayjs from "dayjs";
import { Field, GroupCard } from "./xu-config-fields";
import { applyMult } from "./xu-config-labels";
import { fmtNum, fmtVnDateTime, tierHex } from "./xu-format-helpers";

// Ngưỡng tiền hiển thị 1.200.000 cho dễ đọc; lưu vẫn là số.
const THOUSANDS = (v) => (v === undefined || v === null || v === "" ? "" : String(v).replace(/\B(?=(\d{3})+(?!\d))/g, "."));
const UNTHOUSANDS = (v) => String(v || "").replace(/\./g, "");

const PERKS = [
    ["withdraw", "Rút tiền"],
    ["voucher", "Voucher"],
    ["support", "Hỗ trợ"],
];

function LaunchField({ config, set, launched }) {
    return (
        <Field
            label="Ngày bắt đầu tính hạng (giờ Việt Nam)"
            hint="Đơn được duyệt TRƯỚC mốc này không tính điểm, mọi người bắt đầu từ 0. Để trống = chưa ra mắt hạng (ai cũng là hạng 1, không có hệ số)."
        >
            <DatePicker showTime format="DD/MM/YYYY HH:mm" value={config.rankLaunchAt ? dayjs.unix(config.rankLaunchAt) : null} onChange={(d) => set({ rankLaunchAt: d ? d.unix() : null })} />
            {launched && (
                <Alert className="mt-2 max-w-xl" type="warning" showIcon message={`Đã ra mắt từ ${fmtVnDateTime(config.rankLaunchAt)}.`}
                    description="Đổi ngày sau khi đã ra mắt sẽ làm điểm hạng của mọi người bị tính lại theo mốc mới (có thể có người bị hạ hạng hàng loạt). Không thể thêm/bớt số hạng." />
            )}
        </Field>
    );
}

export default function XuConfigRankCard({ config, set, onReset, launched, defaultsTierCount }) {
    const tiers = config.rankTiers;
    const setTier = (i, patch) => set({ rankTiers: tiers.map((t, idx) => (idx === i ? { ...t, ...patch } : t)) });
    const setPerk = (i, key, v) => setTier(i, { perks: { ...(tiers[i].perks || {}), [key]: v } });
    const notAscending = tiers.some((t, i) => i > 0 && !(Number(t.min) > Number(tiers[i - 1].min)));
    const addTier = () => {
        const last = tiers[tiers.length - 1];
        set({ rankTiers: [...tiers, { tier: tiers.length + 1, name: "Hạng mới", min: Number(last.min) + 1000000, mult: last.mult, perks: { ...(last.perks || {}) } }] });
    };
    const sample = config.checkinRewards[config.checkinRewards.length - 1];

    const columns = [
        {
            title: "Hạng", fixed: "left", width: 170,
            render: (_, t, i) => (
                <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full text-white text-xs flex items-center justify-center shrink-0" style={{ background: tierHex(i + 1) }}>{i + 1}</span>
                    <Input size="small" style={{ width: 120 }} maxLength={40} value={t.name} onChange={(e) => setTier(i, { name: e.target.value })} />
                </div>
            ),
        },
        { title: "Ngưỡng (hoàn tiền 90 ngày)", width: 170, render: (_, t, i) => <InputNumber size="small" disabled={i === 0} min={0} step={100000} style={{ width: 150 }} addonAfter="đ" value={t.min} formatter={THOUSANDS} parser={UNTHOUSANDS} onChange={(v) => setTier(i, { min: v || 0 })} /> },
        { title: "Hệ số Xu", width: 110, render: (_, t, i) => <InputNumber size="small" min={1} max={5} step={0.05} style={{ width: 96 }} addonBefore="×" value={t.mult} onChange={(v) => setTier(i, { mult: v || 1 })} /> },
        { title: "Ví dụ", width: 130, render: (_, t) => <span className="whitespace-nowrap">{fmtNum(sample)} → <b>{fmtNum(applyMult(sample, t.mult))}</b> Xu</span> },
        {
            title: "Quyền lợi (chỉ hiển thị trên app)",
            children: PERKS.map(([key, label]) => ({
                title: label, width: 120,
                render: (_, t, i) => <Input size="small" style={{ width: 110 }} value={t.perks?.[key] || ""} onChange={(e) => setPerk(i, key, e.target.value)} />,
            })),
        },
        {
            title: "", width: 44, fixed: "right",
            render: (_, __, i) => !launched && tiers.length > 2 && i === tiers.length - 1
                ? <Button danger type="text" size="small" title="Xoá hạng này" icon={<FontAwesomeIcon icon={faTrash} />} onClick={() => set({ rankTiers: tiers.slice(0, -1) })} /> : null,
        },
    ];
    return (
        <GroupCard
            id="xu-sec-rank" icon={faRankingStar} tone="text-amber-500"
            title="Hạng"
            description="Hạng tính theo tổng tiền hoàn đơn đã duyệt trong 90 ngày gần nhất. Hạng càng cao, Xu nhận được càng nhân lên."
            onReset={onReset}
            resetDisabled={launched && defaultsTierCount !== tiers.length}
            resetHint="Hạng đã ra mắt: không thể đổi số lượng hạng"
        >
            <LaunchField config={config} set={set} launched={launched} />
            {notAscending && <Alert className="mb-3" type="error" showIcon message="Ngưỡng điểm phải tăng dần từ hạng thấp lên cao — hệ thống sẽ không cho lưu." />}
            <Table size="small" rowKey={(_, i) => i} pagination={false} columns={columns} dataSource={tiers} scroll={{ x: "max-content" }} />
            <div className="text-xs text-gray-400 mt-1 mb-3">Cột “Ví dụ”: Xu điểm danh ngày cuối vòng (gốc) → Xu người hạng đó nhận thực, làm tròn xuống.</div>
            {!launched && <Button className="mb-3" onClick={addTier}>+ Thêm hạng</Button>}
            <Field label="Đơn thuộc kênh nào được tính điểm hạng" hint="app = đơn mua qua ứng dụng. Chỉ đổi khi hiểu rõ kênh đơn.">
                <Select mode="tags" style={{ width: 280 }} value={config.rankChannels} onChange={(v) => set({ rankChannels: v })} open={false} tokenSeparators={[",", " "]} />
            </Field>
        </GroupCard>
    );
}
